/**
 * Properties List View
 * 
 * Main page for displaying and managing channel properties (Units Management).
 * Uses useGetMsoolUnits hook to fetch properties data.
 */

import { FC, useCallback, useMemo, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Box, IconButton, Menu, MenuItem, Pagination, Skeleton, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AddIcon from '@mui/icons-material/Add';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';

import { useGetMsoolUnits } from '../../services/mapping.service';
import { IMsoolUnit, ILinkedUnit, ChannelType } from '../../types';
import { PageContainer, HeaderSection, PageTitle, PageDescription } from '../../styles';
import { T_KEYS } from '../../constants';

// Channel logo imports
import AirbnbIcon from '@assets/svg/airbnb.svg?react';
import BookingIcon from '@assets/svg/Booking.svg?react';
import GathernIcon from '@assets/svg/gathren.svg?react';

// Constants
const ITEMS_PER_PAGE = 10;

// Helper to get channel icon
const getChannelIcon = (channelType: ChannelType): JSX.Element | null => {
  switch (channelType) {
    case ChannelType.Airbnb:
      return <AirbnbIcon />;
    case ChannelType.Booking:
      return <BookingIcon />;
    case ChannelType.Gathern:
      return <GathernIcon />;
    default:
      return null;
  }
};

// Get unique channel types from linked units
const getUniqueChannelTypes = (channels: ILinkedUnit[]): ChannelType[] => {
  const types = channels.map((c) => c.channelType);
  return [...new Set(types)];
};

// Styled Components
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const StatCard = styled.div`
  flex: 1;
  background: white;
  border: 1px solid #eaedf1;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: flex-end;
  gap: 76px;
`;

const StatIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5b3fff;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const StatInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 18px;
  align-items: flex-end;
  text-align: right;
`;

const StatLabel = styled.p`
  font-family: 'Tajawal', sans-serif;
  font-size: 14px;
  color: #435363;
  margin: 0;
`;

const StatValue = styled.p`
  font-family: 'Tajawal', sans-serif;
  font-weight: 700;
  font-size: 32px;
  color: #5b3fff;
  margin: 0;
`;

const AddUnitsCard = styled.div`
  background: #5b3fff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  min-width: 280px;

  &:hover {
    background: #4a2fe6;
  }
`;

const AddIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const AddUnitsContent = styled.div`
  flex: 1;
  text-align: right;
`;

const AddUnitsTitle = styled.h3`
  font-family: 'Tajawal', sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: white;
  margin: 0 0 4px 0;
`;

const AddUnitsDescription = styled.p`
  font-family: 'Tajawal', sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
`;

const PropertiesSection = styled.div`
  background: white;
  border: 1px solid #e9e9e7;
  border-radius: 16px;
  overflow: hidden;
`;

const PropertiesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e9e9e7;
`;

const PropertiesTitle = styled.h2`
  font-family: 'Tajawal', sans-serif;
  font-weight: 700;
  font-size: 18px;
  color: #262626;
  margin: 0;
`;

const SearchField = styled(TextField)`
  width: 300px;

  .MuiOutlinedInput-root {
    border-radius: 24px;
    background: #f7f7f7;
    
    fieldset {
      border: 1px solid #e9e9e7;
    }

    &:hover fieldset {
      border-color: #5b3fff;
    }

    &.Mui-focused fieldset {
      border-color: #5b3fff;
    }
  }

  .MuiInputBase-input {
    padding: 12px 16px;
    font-family: 'Tajawal', sans-serif;
    font-size: 14px;
  }
`;

const PropertiesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #fafafa;
  border-bottom: 1px solid #e9e9e7;
`;

const TableHeaderCell = styled.th`
  font-family: 'Tajawal', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #697586;
  padding: 16px 24px;
  text-align: right;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f2f4f7;
  transition: background 0.2s ease;

  &:hover {
    background: #fafafa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 16px 24px;
  vertical-align: middle;
`;

const PropertyCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PropertyImage = styled.div`
  width: 64px;
  height: 48px;
  background: #f2f4f7;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PropertyName = styled.span`
  font-family: 'Tajawal', sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #262626;
`;

const PropertyAddress = styled.span`
  font-family: 'Tajawal', sans-serif;
  font-size: 14px;
  color: #697586;
`;

const ChannelLogos = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ChannelLogo = styled.div<{ $bgColor?: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${(props) => props.$bgColor || '#f2f4f7'};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid #e9e9e7;
`;

const ResultsInfo = styled.span`
  font-family: 'Tajawal', sans-serif;
  font-size: 14px;
  color: #697586;
`;

const LoadingContainer = styled.div`
  padding: 24px;
`;

const EmptyState = styled.div`
  padding: 64px 24px;
  text-align: center;
`;

const EmptyStateText = styled.p`
  font-family: 'Tajawal', sans-serif;
  font-size: 16px;
  color: #697586;
  margin: 0;
`;

// Property Row Component
interface PropertyRowProps {
  property: IMsoolUnit;
  onMenuOpen: (event: React.MouseEvent<HTMLButtonElement>, property: IMsoolUnit) => void;
}

const PropertyRowComponent: FC<PropertyRowProps> = memo(({ property, onMenuOpen }) => {
  const channelTypes = useMemo(() => getUniqueChannelTypes(property.channels ?? []), [property.channels]);

  return (
    <TableRow>
      <TableCell>
        <IconButton size="small" onClick={(e) => onMenuOpen(e, property)}>
          <MoreHorizIcon />
        </IconButton>
      </TableCell>
      <TableCell>
        <ChannelLogos>
          {channelTypes.map((type) => (
            <ChannelLogo key={type}>
              {getChannelIcon(type)}
            </ChannelLogo>
          ))}
        </ChannelLogos>
      </TableCell>
      <TableCell>
        <PropertyAddress>-</PropertyAddress>
      </TableCell>
      <TableCell>
        <PropertyCell>
          <PropertyName>{property.propertyName}</PropertyName>
          <PropertyImage>
            {property.imageUrl ? (
              <img src={property.imageUrl} alt={property.propertyName ?? ''} />
            ) : (
              <Box 
                sx={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: '#f2f4f7'
                }}
              >
                <HomeOutlinedIcon sx={{ color: '#9ca3af' }} />
              </Box>
            )}
          </PropertyImage>
        </PropertyCell>
      </TableCell>
    </TableRow>
  );
});

PropertyRowComponent.displayName = 'PropertyRowComponent';

// Main Component
const PropertiesListView: FC = () => {
  const navigate = useNavigate();
  const { t, i18n: { language } } = useTranslation();
  const isRtl = language === 'ar';

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedProperty, setSelectedProperty] = useState<IMsoolUnit | null>(null);

  // Fetch data
  const { data, isLoading, isError } = useGetMsoolUnits();

  // Computed values
  const properties = useMemo(() => data?.units || [], [data]);
  
  const filteredProperties = useMemo(() => {
    if (!searchQuery.trim()) return properties;
    const query = searchQuery.toLowerCase();
    return properties.filter((p) => 
      (p.propertyName ?? '').toLowerCase().includes(query)
    );
  }, [properties, searchQuery]);

  const paginatedProperties = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProperties.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProperties, currentPage]);

  const totalPages = Math.ceil(filteredProperties.length / ITEMS_PER_PAGE);

  // Stats
  const stats = useMemo(() => {
    const totalProperties = properties.length;
    const connectedProperties = properties.filter((p) => (p.channels ?? []).length > 0).length;
    const uniqueChannels = new Set(
      properties.flatMap((p) => (p.channels ?? []).map((c) => c.channelType))
    ).size;
    return { totalProperties, connectedProperties, uniqueChannels };
  }, [properties]);

  // Handlers
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  }, []);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>, property: IMsoolUnit) => {
    setMenuAnchor(event.currentTarget);
    setSelectedProperty(property);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setSelectedProperty(null);
  }, []);

  const handleAddUnits = useCallback(() => {
    navigate('/channels/units-mapping');
  }, [navigate]);

  const handleManageMapping = useCallback(() => {
    handleMenuClose();
    navigate('/channels/units-mapping');
  }, [handleMenuClose, navigate]);

  // Render loading state
  const renderLoading = () => (
    <LoadingContainer>
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} variant="rectangular" height={64} sx={{ mb: 2, borderRadius: 2 }} />
      ))}
    </LoadingContainer>
  );

  // Render empty state
  const renderEmpty = () => (
    <EmptyState>
      <EmptyStateText>
        {t(T_KEYS.NO_PROPERTIES_YET)}
      </EmptyStateText>
    </EmptyState>
  );

  return (
    <PageContainer>
      <HeaderSection>
        <PageTitle>{t(T_KEYS.UNITS_MANAGEMENT)}</PageTitle>
        <PageDescription>
          {t(T_KEYS.UNITS_MANAGEMENT_DESC)}
        </PageDescription>
      </HeaderSection>

      <ContentContainer>
        {/* Stats and Add Units Row */}
        <StatsContainer>
          <AddUnitsCard onClick={handleAddUnits}>
            <AddUnitsContent>
              <AddUnitsTitle>{t(T_KEYS.ADD_UNITS)}</AddUnitsTitle>
              <AddUnitsDescription>
                {t(T_KEYS.ADD_UNITS_DESC)}
              </AddUnitsDescription>
            </AddUnitsContent>
            <AddIconWrapper>
              <AddIcon />
            </AddIconWrapper>
          </AddUnitsCard>

          <StatCard>
            <StatIcon>
              <HomeOutlinedIcon />
            </StatIcon>
            <StatInfo>
              <StatLabel>{t(T_KEYS.NUMBER_OF_PROPERTIES)}</StatLabel>
              <StatValue>{String(stats.totalProperties).padStart(2, '0')}</StatValue>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon>
              <LinkIcon />
            </StatIcon>
            <StatInfo>
              <StatLabel>{t(T_KEYS.CONNECTED_PROPERTIES)}</StatLabel>
              <StatValue>{String(stats.connectedProperties).padStart(2, '0')}</StatValue>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon>
              <LayersOutlinedIcon />
            </StatIcon>
            <StatInfo>
              <StatLabel>{t(T_KEYS.NUMBER_OF_CHANNELS)}</StatLabel>
              <StatValue>{String(stats.uniqueChannels).padStart(2, '0')}</StatValue>
            </StatInfo>
          </StatCard>
        </StatsContainer>

        {/* Properties List */}
        <PropertiesSection>
          <PropertiesHeader>
            <SearchField
              placeholder={t(T_KEYS.SEARCH)}
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
            />
            <PropertiesTitle>{t(T_KEYS.PROPERTIES_LIST)}</PropertiesTitle>
          </PropertiesHeader>

          {isLoading && renderLoading()}

          {!isLoading && filteredProperties.length === 0 && renderEmpty()}

          {!isLoading && filteredProperties.length > 0 && (
            <>
              <PropertiesTable>
                <TableHeader>
                  <tr>
                    <TableHeaderCell style={{ width: '60px' }}></TableHeaderCell>
                    <TableHeaderCell>{t(T_KEYS.CHANNELS)}</TableHeaderCell>
                    <TableHeaderCell>{t(T_KEYS.ADDRESS)}</TableHeaderCell>
                    <TableHeaderCell>{t(T_KEYS.PROPERTY)}</TableHeaderCell>
                  </tr>
                </TableHeader>
                <TableBody>
                  {paginatedProperties.map((property) => (
                    <PropertyRowComponent
                      key={property.propertyId}
                      property={property}
                      onMenuOpen={handleMenuOpen}
                    />
                  ))}
                </TableBody>
              </PropertiesTable>

              <PaginationContainer>
                <ResultsInfo>
                  {t(T_KEYS.RESULTS_PER_PAGE)} {ITEMS_PER_PAGE}
                </ResultsInfo>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontFamily: 'Tajawal, sans-serif',
                    },
                    '& .Mui-selected': {
                      backgroundColor: '#5b3fff !important',
                      color: 'white',
                    },
                  }}
                />
              </PaginationContainer>
            </>
          )}
        </PropertiesSection>
      </ContentContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleManageMapping}>
          {t(T_KEYS.MANAGE_MAPPING)}
        </MenuItem>
      </Menu>
    </PageContainer>
  );
};

export default memo(PropertiesListView);
